import { ProgramProgressCell } from '../types/domain';
import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import { buildLearningContentScopeVersion } from './learningContentCacheVersion';
import { deviceCacheStorage } from './deviceCacheStorage';

const PROGRAM_PROGRESS_CACHE_VERSION = 'v5';

export type ProgramProgressCellsCacheCoverage = 'full' | 'partial';

type ProgramProgressCachePayload = {
  rows: ProgramProgressCell[];
  savedAt: number;
  /**
   * `full`: 학기 전량(또는 동등) 스냅샷. `partial`: 구간 fetch만으로 채워져 일부 주만 있을 수 있음.
   * 필드 없음(구버전)은 `full`로 간주.
   */
  coverage?: ProgramProgressCellsCacheCoverage;
  /** `count:max(updated_at)` — head 비교용(전량 캐시) */
  contentVersion?: string;
};

function storageKey(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): string {
  return `cp.programProgressCells.${PROGRAM_PROGRESS_CACHE_VERSION}.${workspaceId}.${academicYear}.${termNumber}.${gradeId}`;
}

function normalizeRows(raw: unknown): ProgramProgressCell[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((row): row is ProgramProgressCell => !!row && typeof row === 'object');
}

function rowIdentity(row: Pick<ProgramProgressCell, 'monday_iso' | 'weekday_key' | 'slot_index'>): string {
  return `${row.monday_iso}|${row.weekday_key}|${row.slot_index}`;
}

export function loadProgramProgressCellsCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): {
  found: boolean;
  rows: ProgramProgressCell[];
  coverage: ProgramProgressCellsCacheCoverage;
  contentVersion: string | null;
} {
  if (!workspaceId || !gradeId || typeof window === 'undefined') {
    return { found: false, rows: [], coverage: 'full', contentVersion: null };
  }
  try {
    const raw = deviceCacheStorage.getItem(storageKey(workspaceId, academicYear, termNumber, gradeId));
    if (!raw) return { found: false, rows: [], coverage: 'full', contentVersion: null };
    const parsed = JSON.parse(raw) as ProgramProgressCachePayload | ProgramProgressCell[];
    if (Array.isArray(parsed)) {
      const rows = normalizeRows(parsed);
      return {
        found: true,
        rows,
        coverage: 'full',
        contentVersion: buildLearningContentScopeVersion(rows),
      };
    }
    if (!parsed || typeof parsed !== 'object') {
      return { found: false, rows: [], coverage: 'full', contentVersion: null };
    }
    const payload = parsed as ProgramProgressCachePayload;
    const cov: ProgramProgressCellsCacheCoverage =
      payload.coverage === 'partial' ? 'partial' : 'full';
    const rows = normalizeRows(payload.rows);
    const contentVersion =
      typeof payload.contentVersion === 'string' && payload.contentVersion.trim()
        ? payload.contentVersion.trim()
        : cov === 'full'
          ? buildLearningContentScopeVersion(rows)
          : null;
    return { found: true, rows, coverage: cov, contentVersion };
  } catch {
    return { found: false, rows: [], coverage: 'full', contentVersion: null };
  }
}

/** 학기 전량 캐시에서 월요일(YYYY-MM-DD) 구간만 잘라 program 구간 로드에 사용 */
export function filterProgramProgressCellsByMondayIsoInclusive(
  rows: ProgramProgressCell[],
  from: string,
  to: string
): ProgramProgressCell[] {
  const f = from.trim();
  const t = to.trim();
  if (!f || !t) return [];
  return rows.filter((r) => {
    const m = (r.monday_iso ?? '').trim();
    return m >= f && m <= t;
  });
}

export function saveProgramProgressCellsCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  rows: ProgramProgressCell[],
  coverage: ProgramProgressCellsCacheCoverage = 'full',
  options?: { contentVersion?: string }
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined' || !canWriteSchoolScopedDataCache(workspaceId)) return;
  try {
    const contentVersion =
      (options?.contentVersion ?? '').trim() ||
      (coverage === 'full' ? buildLearningContentScopeVersion(rows) : '');
    const payload: ProgramProgressCachePayload = {
      rows,
      savedAt: Date.now(),
      coverage,
      ...(contentVersion ? { contentVersion } : {}),
    };
    deviceCacheStorage.setItem(storageKey(workspaceId, academicYear, termNumber, gradeId), JSON.stringify(payload));
  } catch {
    // ignore quota / private mode write errors
  }
}

export function clearProgramProgressCellsCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return;
  try {
    deviceCacheStorage.removeItem(storageKey(workspaceId, academicYear, termNumber, gradeId));
  } catch {
    // ignore
  }
}

export function mergeProgramProgressCellsCacheRows(
  existingRows: ProgramProgressCell[],
  updatedRows: ProgramProgressCell[]
): ProgramProgressCell[] {
  if (updatedRows.length === 0) return existingRows;
  const updatedByIdentity = new Map<string, ProgramProgressCell>();
  updatedRows.forEach((row) => {
    updatedByIdentity.set(rowIdentity(row), row);
  });
  const merged = existingRows.map((row) => updatedByIdentity.get(rowIdentity(row)) ?? row);
  const existingIdentitySet = new Set(existingRows.map((row) => rowIdentity(row)));
  updatedRows.forEach((row) => {
    if (!existingIdentitySet.has(rowIdentity(row))) {
      merged.push(row);
    }
  });
  return merged;
}
